import React, { useEffect, useState } from 'react';
import api from '../services/api';
import {
  RiUserHeartLine, RiPhoneLine, RiHistoryLine,
  RiMedalLine, RiCheckboxCircleLine, RiTimeLine,
} from 'react-icons/ri';

interface Client {
  id: number;
  prenom: string;
  nom: string;
  telephone: string;
  created_at: string;
}

interface Service {
  nom: string;
  prix: number;
}

interface ServiceSelection {
  service: Service;
}

interface Session {
  id: number;
  reference: string;
  status: string;
  created_at: string;
  service_selections: ServiceSelection[];
}

const statusBadge = (status: string) => {
  const map: Record<string, string> = {
    'terminée': 'bg-green-50 text-green-700',
    'en cours': 'bg-blue-50 text-blue-700',
    'créée': 'bg-yellow-50 text-yellow-700',
  };
  return map[status] ?? 'bg-gray-100 text-gray-500';
};

const AdminClients: React.FC = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [history, setHistory] = useState<Session[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  useEffect(() => {
    api.get('/clients/').then(r => setClients(r.data)).catch(console.error);
  }, []);

  const loadHistory = async (client: Client) => {
    setSelectedClient(client);
    setLoadingHistory(true);
    try {
      const res = await api.get(`/clients/${client.id}/history`);
      setHistory(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingHistory(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto">
      {/* Page header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Clientes</h1>
        <p className="text-sm text-gray-500 mt-1">Consultez les profils clients et leur historique de soins</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Client list */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100">
              <h2 className="text-sm font-semibold text-gray-800">
                Liste des clientes
                <span className="ml-2 text-xs font-normal text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full">
                  {clients.length}
                </span>
              </h2>
            </div>
            {clients.length === 0 ? (
              <p className="px-5 py-8 text-center text-gray-400 text-sm">Aucune cliente enregistrée</p>
            ) : (
              <ul className="divide-y divide-gray-100 max-h-[calc(100vh-16rem)] overflow-y-auto">
                {clients.map(client => (
                  <li key={client.id}>
                    <button
                      onClick={() => loadHistory(client)}
                      className={`w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-gray-50 transition-colors ${
                        selectedClient?.id === client.id ? 'bg-brand-50 border-l-2 border-brand-500' : ''
                      }`}
                    >
                      <div className="w-9 h-9 rounded-full bg-brand-100 flex items-center justify-center flex-shrink-0">
                        <RiUserHeartLine className="text-brand-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium truncate ${
                          selectedClient?.id === client.id ? 'text-brand-700' : 'text-gray-900'
                        }`}>
                          {client.prenom} {client.nom}
                        </p>
                        <p className="text-xs text-gray-400 flex items-center gap-1 mt-0.5">
                          <RiPhoneLine className="text-xs" />
                          {client.telephone}
                        </p>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* History panel */}
        <div className="lg:col-span-3">
          {!selectedClient ? (
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm h-64 flex flex-col items-center justify-center text-gray-400">
              <RiHistoryLine className="text-4xl mb-3 text-gray-300" />
              <p className="text-sm">Sélectionnez une cliente pour voir son historique</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
              {/* Header */}
              <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                <div>
                  <h2 className="text-sm font-semibold text-gray-800">
                    {selectedClient.prenom} {selectedClient.nom}
                  </h2>
                  <p className="text-xs text-gray-400 mt-0.5 flex items-center gap-1">
                    <RiPhoneLine /> {selectedClient.telephone}
                  </p>
                </div>
                {/* Loyalty badge */}
                <div className="flex items-center gap-1.5 bg-amber-50 border border-amber-200 text-amber-700 text-xs font-semibold px-3 py-1.5 rounded-full">
                  <RiMedalLine />
                  {history.length * 10} pts fidélité
                </div>
              </div>

              {/* Sessions */}
              {loadingHistory ? (
                <div className="px-5 py-8 text-center text-gray-400 text-sm">Chargement…</div>
              ) : history.length === 0 ? (
                <div className="px-5 py-8 text-center text-gray-400 text-sm">
                  Aucun historique de soins pour cette cliente.
                </div>
              ) : (
                <ul className="divide-y divide-gray-100 max-h-[calc(100vh-16rem)] overflow-y-auto">
                  {history.map(session => {
                    const total = session.service_selections.reduce(
                      (sum, sel) => sum + (sel.service?.prix ?? 0), 0
                    );
                    return (
                      <li key={session.id} className="px-5 py-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="text-sm font-medium text-gray-900">
                                Réf. {session.reference}
                              </p>
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusBadge(session.status)}`}>
                                {session.status}
                              </span>
                            </div>
                            <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                              <RiTimeLine />
                              {new Date(session.created_at).toLocaleString('fr-FR', {
                                day: '2-digit', month: 'short', year: 'numeric',
                                hour: '2-digit', minute: '2-digit',
                              })}
                            </p>
                            {session.service_selections.length > 0 && (
                              <ul className="mt-2 space-y-0.5">
                                {session.service_selections.map((sel, idx) => (
                                  <li key={idx} className="text-xs text-gray-600 flex items-center gap-1">
                                    <RiCheckboxCircleLine className="text-brand-400 flex-shrink-0" />
                                    {sel.service?.nom}
                                    <span className="text-gray-400 ml-auto">
                                      {sel.service?.prix?.toLocaleString()} F
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className="text-sm font-bold text-gray-900">{total.toLocaleString()} F</p>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminClients;
